import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}
interface State {
  err: Error | null;
}

// Top-level boundary so a render error in any page does not whitewash the
// app. Sentry / OTel hookup will land in WP-G; for now we surface the error
// inline with a "Reload" CTA so the user has a clear recovery path.
export class ErrorBoundary extends Component<Props, State> {
  public override state: State = { err: null };

  public static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  public override componentDidCatch(err: Error, info: ErrorInfo): void {
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.error("[ErrorBoundary]", err, info.componentStack);
    }
  }

  public override render(): ReactNode {
    if (this.state.err) {
      return (
        <div className="error-boundary" role="alert">
          <div className="error-boundary-card">
            <h2>Something went wrong</h2>
            <p style={{ color: "var(--text-3)", fontSize: 13, margin: 0 }}>
              An unexpected error stopped this page from rendering. Try
              reloading. If it keeps happening, copy the message below and
              send it to support.
            </p>
            <pre>{this.state.err.message || String(this.state.err)}</pre>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => window.location.reload()}
                data-testid="error-boundary-reload"
              >
                Reload
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => this.setState({ err: null })}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
